import { motion } from "framer-motion";
import { ImageWithFallback } from "../common/ImageWithFallback";
import { useState, useRef } from "react";
import { useLanguage } from "../LanguageContext";

export default function AboutUs() {
  const { t, currentLanguage } = useLanguage();
  // Single state to track which team member is expanded
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  
  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // TEMPORARY: Team members data updated with actual photos (Update #13 - 2026-01-04)
  // Photos format: X_YY_ZZZ where X=order, YY=name, ZZZ=title
  const teamMembers = [
    {
      id: "member1",
      name: "Honami Murayama",
      position: "Founder",
      image: "/images/about_us/team_members/1_Honami Murayama_Founder.png",
      shortBio: t('teamMembers.member1ShortBio'),
      fullBio: t('teamMembers.member1FullBio'),
      expertise: [
        t('teamMembers.member1Expertise1'),
        t('teamMembers.member1Expertise2'),
        t('teamMembers.member1Expertise3')
      ]
    },
    {
      id: "member2",
      name: "Jiawei Zhao",
      position: "Chief Executive Officer",
      image: "/images/about_us/team_members/2_Jiawei Zhao_Chief Executive Officer.png",
      shortBio: t('teamMembers.member2ShortBio'),
      fullBio: t('teamMembers.member2FullBio'),
      expertise: [
        t('teamMembers.member2Expertise1'),
        t('teamMembers.member2Expertise2'),
        t('teamMembers.member2Expertise3')
      ]
    },
    {
      id: "member3",
      name: "Jin Jiao",
      position: "Chief Technology Officer\nChief Strategy Officer",
      image: "/images/about_us/team_members/3_Jin Jiao_Chief Technology Officer.png",
      shortBio: t('teamMembers.member4ShortBio'),
      fullBio: t('teamMembers.member4FullBio'),
      expertise: [
        t('teamMembers.member4Expertise1'),
        t('teamMembers.member4Expertise2'),
        t('teamMembers.member4Expertise3')
      ]
    },
    {
      id: "member4",
      name: "Y.K. Yuan",
      position: "Chief Financial Officer",
      image: "/images/about_us/team_members/4_Y.K. Yuan_Chief Financial Officer.png",
      shortBio: t('teamMembers.member3ShortBio'),
      fullBio: t('teamMembers.member3FullBio'),
      expertise: [
        t('teamMembers.member3Expertise1'),
        t('teamMembers.member3Expertise2'),
        t('teamMembers.member3Expertise3')
      ]
    },
    {
      id: "member5",
      name: "Irene Chang",
      position: "Creative Director",
      image: "/images/about_us/team_members/5_Irene Chang_Creative Director.jpg",
      shortBio: t('teamMembers.member5ShortBio'),
      fullBio: t('teamMembers.member5FullBio'),
      expertise: [
        t('teamMembers.member5Expertise1'),
        t('teamMembers.member5Expertise2'),
        t('teamMembers.member5Expertise3')
      ]
    },
    {
      id: "member6",
      name: "Fuse",
      position: "Thailand Sales Leader",
      image: "/images/about_us/team_members/6_Fuse_Thailand Sales Leader.jpg",
      shortBio: t('teamMembers.member7ShortBio'),
      fullBio: t('teamMembers.member7FullBio'),
      expertise: [
        t('teamMembers.member7Expertise1'),
        t('teamMembers.member7Expertise2'),
        t('teamMembers.member7Expertise3')
      ]
    },
    {
      id: "member7",
      name: "Thao Tran",
      position: "Vietnam Business Leader",
      image: "/images/about_us/team_members/7_Thao Tran_Vietnam Business Leader.png",
      shortBio: "Leading our Vietnam market expansion",
      fullBio: "Thao Tran leads our business development and operations in Vietnam, bringing deep local market knowledge and strong client relationships.",
      expertise: [
        "Business Development",
        "Market Strategy",
        "Client Relations"
      ]
    },
    {
      id: "member8",
      name: "Thai Hoang",
      position: "Vietnam Sales Manager",
      image: "/images/about_us/team_members/8_Thai Hoang_Vietnam Sales Manager.png",
      shortBio: "Managing sales operations in Vietnam",
      fullBio: "Thai Hoang manages our sales operations in Vietnam, building client relationships and driving business growth.",
      expertise: [
        "Sales Management",
        "Client Relations",
        "Business Development"
      ]
    },
    {
      id: "member9",
      name: "Nhi Nguyen",
      position: "Vietnam Sales Manager",
      image: "/images/about_us/team_members/9_Nhi Nguyen_Vietnam Sales Manager.png",
      shortBio: "Driving sales growth in Vietnam",
      fullBio: "Nhi Nguyen drives sales growth and client acquisition in the Vietnam market.",
      expertise: [
        "Sales Strategy",
        "Client Acquisition",
        "Market Development"
      ]
    },
    {
      id: "member10",
      name: "Mary Liao",
      position: "Creative Manager",
      image: "/images/about_us/team_members/10_Mary Liao_Creative Manager.jpg",
      shortBio: t('teamMembers.member6ShortBio'),
      fullBio: t('teamMembers.member6FullBio'),
      expertise: [
        t('teamMembers.member6Expertise1'),
        t('teamMembers.member6Expertise2'),
        t('teamMembers.member6Expertise3')
      ]
    },
    {
      id: "member11",
      name: "Hwen",
      position: "Creative Manager",
      image: "/images/about_us/team_members/11_Hwen_Creative Manager.png",
      shortBio: "Managing creative projects and team",
      fullBio: "Hwen leads creative projects and manages the creative team to deliver exceptional design solutions.",
      expertise: [
        "Creative Direction",
        "Project Management",
        "Team Leadership"
      ]
    },
    {
      id: "member12",
      name: "Usa",
      position: "Design Manager",
      image: "/images/about_us/team_members/12_Usa_Design Manager.jpg",
      shortBio: "Leading design initiatives",
      fullBio: "Usa manages design projects and ensures high-quality visual output across all client deliverables.",
      expertise: [
        "Design Management",
        "Visual Design",
        "Brand Development"
      ]
    }
  ];

  // TEMPORARY: Expand functionality disabled (Update #12 - 2025-08-20)
  // Original click handler commented out for easy reversal
  const handleMemberClick = (_memberId: string) => {
    // Expansion temporarily disabled - do nothing
    return;
  };
  
  /* ORIGINAL CODE - UNCOMMENT WHEN RE-ENABLING EXPAND FUNCTION
  const handleMemberClick = (memberId: string) => {
    if (expandedMember === memberId) {
      setExpandedMember(null); // Collapse if already expanded
    } else {
      setExpandedMember(memberId); // Expand the clicked member
      
      // Scroll to the expanded member card after a short delay to allow animation to start
      setTimeout(() => {
        const expandedCard = document.querySelector(`[data-member-id="${memberId}"]`);
        if (expandedCard) {
          // Calculate proper scroll position
          const headerOffset = 200; // Account for fixed header + some padding
          const elementPosition = expandedCard.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150); // Slightly longer delay to ensure DOM update
    }
  };
  */

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 320; // Width of one card (300) + gap (20)
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 320; // Width of one card (300) + gap (20)
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pt-26">
      <main className="relative">
        {/* Hero Section */}
        <section className="pt-16 pb-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-center space-y-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-4xl lg:text-6xl leading-tight text-foreground">
                  <span className="block">
                    {t('about.heroTitle')}
                    <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                      {currentLanguage !== 'ja' && " "}{t('about.heroTitleHighlight').split('\n').map((line, i, arr) => (
                        <span key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </span>
                      ))}
                    </span>
                  </span>
                </h1>

                <motion.p
                  className="text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('about.heroDescription')}
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="absolute inset-0">
            <motion.div
              className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent"
              animate={{ x: ["-100%", "100%"] }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear",
              }}
            ></motion.div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Content */}
              <motion.div
                className="space-y-8"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl lg:text-5xl text-foreground leading-tight">
                  {t('about.ourStory')}
                </h2>
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    {t('about.ourStoryPara1')}
                  </p>
                  <p>
                    {t('about.ourStoryPara2')}
                  </p>
                  <p>
                    {t('about.ourStoryPara3')}
                  </p>
                  <p>
                    {t('about.ourStoryPara4')}
                  </p>
                </div>
              </motion.div>

              {/* Right Content - Statistics */}
              <div
                className="relative"
              >
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { number: t('about.stat1Number'), label: t('about.stat1Label') },
                    { number: t('about.stat2Number'), label: t('about.stat2Label') },
                    { number: t('about.stat3Number'), label: t('about.stat3Label') },
                    { number: t('about.stat4Number'), label: t('about.stat4Label') }
                  ].map((stat, index) => (
                    <div
                      key={index}
                      className="bg-white border border-slate-200/60 rounded-xl p-6 text-center"
                    >
                      <div className={`text-2xl lg:text-3xl mb-2 ${index % 2 === 0 ? 'text-slate-800' : 'text-cyan-600'}`}>
                        {stat.number}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {stat.label.split('\n').map((line, i, arr) => (
                          <span key={i}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mission, Vision, Value - moved here within About Us section */}
            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 mt-16">
              {/* Mission */}
              <div
                className="space-y-8"
              >
                <div className="bg-white border border-slate-200/60 rounded-xl p-8 relative overflow-hidden h-full">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-slate-800/5 to-transparent rounded-bl-full"></div>
                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-foreground mb-6">
                      {t('about.ourMission')}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t('about.ourMissionText')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Vision */}
              <div
                className="space-y-8"
              >
                <div className="bg-white border border-slate-200/60 rounded-xl p-8 relative overflow-hidden h-full">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-cyan-400/5 to-transparent rounded-bl-full"></div>
                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-foreground mb-6">
                      {t('about.ourVision')}
                    </h3>
                    <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {t('about.ourVisionText')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Value */}
              <div
                className="space-y-8"
              >
                <div className="bg-white border border-slate-200/60 rounded-xl p-8 relative overflow-hidden h-full">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-slate-800/5 to-transparent rounded-bl-full"></div>
                  <div className="relative z-10">
                    <h3 className="text-2xl lg:text-3xl text-foreground mb-6">
                      {t('about.ourValue')}
                    </h3>
                    <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {t('about.ourValueText')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Message from the Founder Section */}
        <section className="py-20 bg-gradient-to-b from-slate-100/10 to-slate-50/30 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className={`text-3xl lg:text-5xl text-foreground ${currentLanguage === 'ja' ? 'mb-[60px]' : 'mb-10'}`}>
                {t('about.founderMessage')}
              </h2>

              <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-start">
                {/* Left column - Message text */}
                <div className="space-y-4 text-muted-foreground">
                  <p>{t('about.founderMessagePara1')}</p>
                  <p>{t('about.founderMessagePara2')}</p>
                  <p>{t('about.founderMessagePara3')}</p>
                  <p>{t('about.founderMessagePara4')}</p>
                  <p>{t('about.founderMessagePara5')}</p>
                  <p>{t('about.founderMessagePara6')}</p>
                </div>

                {/* Right column - Founder photo and signature */}
                <div className="flex flex-col space-y-4 items-center">
                  <img
                    src="/images/about_us/founder_message.png"
                    alt="Honami Murayama"
                    className={`w-full h-auto rounded-lg ${currentLanguage === 'ja' ? 'max-w-[185px]' : 'max-w-[240px]'}`}
                  />
                  <div className="text-center">
                    <p className="font-medium text-foreground whitespace-pre-line">{t('about.founderName')}</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{t('about.founderTitle')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Team Members Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-left space-y-6 mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('about.meetOurTeam')}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('about.meetOurTeamDesc')}
              </p>
            </motion.div>

            {/* Horizontal Scrolling Team Members */}
            <div className="relative">
              {/* Navigation Arrows */}
              <div className="absolute inset-y-0 left-0 z-10 flex items-center">
                <motion.button
                  onClick={scrollLeft}
                  className="bg-white/90 backdrop-blur-sm border border-slate-200/60 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30 group -ml-4"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-slate-600 group-hover:text-cyan-600 transition-colors duration-300">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.button>
              </div>

              <div className="absolute inset-y-0 right-0 z-10 flex items-center">
                <motion.button
                  onClick={scrollRight}
                  className="bg-white/90 backdrop-blur-sm border border-slate-200/60 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30 group -mr-4"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-slate-600 group-hover:text-cyan-600 transition-colors duration-300">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.button>
              </div>

              {/* Scrollable Container */}
              <motion.div
                ref={scrollContainerRef}
                className="flex space-x-6 overflow-x-auto pb-6 scroll-smooth px-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                style={{ 
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                } as React.CSSProperties}
              >
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    data-member-id={member.id}
                    className="flex-shrink-0 w-80 bg-white border border-slate-200/60 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:border-cyan-400/30 group"
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handleMemberClick(member.id)}
                  >
                    <div className="text-center space-y-4">
                      {/* Profile Image - Updated with face-centering (Update #12) */}
                      <motion.div
                        className="relative mx-auto w-24 h-24 rounded-full overflow-hidden group-hover:scale-110 transition-transform duration-300"
                        whileHover={{ scale: 1.1 }}
                      >
                        <ImageWithFallback
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover object-center"
                          style={{ 
                            // Face-centering adjustments per team member
                            objectPosition: member.id === 'member1' ? 'center 20%' :
                                          member.id === 'member2' ? 'center 30%' :
                                          member.id === 'member3' ? 'center 25%' :
                                          member.id === 'member4' ? 'center 30%' :
                                          member.id === 'member5' ? 'center 35%' :
                                          member.id === 'member6' ? 'center 30%' :
                                          member.id === 'member7' ? 'center 25%' :
                                          member.id === 'member8' ? 'center 30%' :
                                          'center center',
                            // Scale adjustments for specific members
                            transform: member.id === 'member6' ? 'scale(1.5)' :  // Fuse - zoom out more to make face bigger
                                      'scale(1)'
                          }}
                        />
                        {/* TEMPORARY: Click to expand disabled (Update #12) */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-800/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        {/* Original expand message commented out
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="text-white text-xs bg-black/50 px-2 py-1 rounded">{t('about.clickToExpand')}</span>
                        </div>
                        */}
                      </motion.div>

                      {/* Member Info */}
                      <div>
                        <h3 className="text-xl text-foreground group-hover:text-slate-800 transition-colors duration-300">
                          {member.name}
                        </h3>
                        <p className="text-sm text-cyan-600">
                          {member.position.split('\n').map((line, i, arr) => (
                            <span key={i}>
                              {line}
                              {i < arr.length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Expanded Member Details */}
            {expandedMember && (
              <motion.div
                className="mt-16 relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg max-w-4xl mx-auto"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Triangle pointer */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
                </div>

                {(() => {
                  const member = teamMembers.find(m => m.id === expandedMember);
                  if (!member) return null;

                  return (
                    <>
                      {/* Header */}
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex items-start space-x-6">
                          <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                            <ImageWithFallback
                              src={member.image}
                              alt={member.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h3 className="text-2xl lg:text-3xl text-foreground mb-2">
                              {member.name}
                            </h3>
                            <p className="text-lg text-cyan-600 mb-2">
                              {member.position.split('\n').map((line, i, arr) => (
                                <span key={i}>
                                  {line}
                                  {i < arr.length - 1 && <br />}
                                </span>
                              ))}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedMember(null)}
                          className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6L18 18" />
                          </svg>
                        </button>
                      </div>

                      {/* Full Bio */}
                      <div className="mb-8">
                        <p className="text-muted-foreground leading-relaxed">
                          {member.fullBio}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            )}
          </div>
        </section>

        {/* Company Overview Section */}
        <section className="py-20 bg-gradient-to-b from-slate-100/10 to-slate-50/30 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-left space-y-6 mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('about.companyOverview')}
              </h2>
            </motion.div>

            {/* Company Information Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Company Name */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-center font-medium text-base border-t border-b border-black">
                  {t('about.companyNameLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-t border-b border-slate-200/60">
                  {t('about.companyName')}
                </div>
              </div>

              {/* Representative */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-center font-medium text-base border-b border-black">
                  {t('about.representativeLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-b border-slate-200/60">
                  {t('about.representative')}
                </div>
              </div>

              {/* Paid-in Capital */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-center font-medium text-base border-b border-black">
                  {t('about.paidInCapitalLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-b border-slate-200/60">
                  {t('about.paidInCapital')} <span className="text-gray-400 text-sm ml-2">{t('about.paidInCapitalNote')}</span>
                </div>
              </div>

              {/* Number of Employees */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-center font-medium text-base border-b border-black">
                  {t('about.numberOfEmployeesLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-b border-slate-200/60">
                  {t('about.numberOfEmployees')} <span className="text-gray-400 text-sm ml-2">{t('about.numberOfEmployeesNote')}</span>
                </div>
              </div>

              {/* Head Office */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-center font-medium text-base border-b border-black">
                  {t('about.headOfficeLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-b border-slate-200/60">
                  {t('about.headOfficeAddress')}
                </div>
              </div>

              {/* Global Office */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-start font-medium text-base border-b border-black">
                  {t('about.globalOfficeLabel')}
                </div>
                <div className="p-6 space-y-4 border-b border-slate-200/60">
                  {/* Thailand Office */}
                  <div className="flex items-start text-foreground">
                    <div>
                      <span className="font-medium">-{t('about.thailandOffice')}: </span>
                      {t('about.thailandOfficeAddress')}
                    </div>
                  </div>
                  {/* Vietnam Office */}
                  <div className="flex items-start text-foreground">
                    <div>
                      <span className="font-medium">-{t('about.vietnamOffice')}: </span>
                      {t('about.vietnamOfficeAddress')}
                    </div>
                  </div>
                  {/* China Office */}
                  <div className="flex items-start text-foreground">
                    <div>
                      <span className="font-medium">-{t('about.chinaOffice')}: </span>
                      {t('about.chinaOfficeAddress')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Portfolio */}
              <div className="grid md:grid-cols-[300px_1fr]">
                <div className="text-gray-900 p-6 flex items-start font-medium text-base border-b border-black">
                  {t('about.businessPortfolioLabel')}
                </div>
                <div className="p-6 flex items-center text-foreground border-b border-slate-200/60">
                  {t('about.businessPortfolioText')}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </main>
    </div>
  );
}